import {createApi} from '@reduxjs/toolkit/query/react'
import {baseQuery} from '../base-query'
import type {
  Bucket,
  CreateBucketRequest,
  GetBucketsResponse,
  ShareBucketRequest,
  UpdateBucketRequest,
  UpdateShareRequest,
} from '~/src/models/bucket'

export const bucketApi = createApi({
  reducerPath: 'BucketApi',
  tagTypes: ['Buckets'],
  baseQuery,
  keepUnusedDataFor: 30,
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    getBuckets: builder.query<GetBucketsResponse[], void>({
      providesTags: ['Buckets'],
      query: () => ({url: '/api/bucket/getBuckets', method: 'GET'}),
    }),

    getBucket: builder.query<Bucket, string>({
      providesTags: ['Buckets'],
      query: (id) => ({url: '/api/bucket', method: 'GET', params: {id}}),
    }),

    getBucketByToken: builder.query<Bucket, string>({
      providesTags: ['Buckets'],
      query: (token) => ({url: '/api/bucket/byToken', method: 'GET', params: {token}}),
    }),

    createBucket: builder.mutation<Bucket, CreateBucketRequest>({
      invalidatesTags: ['Buckets'],
      query: (body) => ({url: '/api/bucket', method: 'POST', body}),
    }),

    updateBucket: builder.mutation<Bucket, UpdateBucketRequest>({
      invalidatesTags: ['Buckets'],
      query: (body) => ({url: '/api/bucket', method: 'PUT', body}),
    }),

    deleteBucket: builder.mutation<void, string>({
      invalidatesTags: ['Buckets'],
      query: (id) => ({url: `/api/bucket/${id}`, method: 'DELETE'}),
    }),

    shareBucket: builder.mutation<Bucket, ShareBucketRequest>({
      invalidatesTags: ['Buckets'],
      query: (body) => ({url: '/api/bucket/share', method: 'POST', body}),
    }),

    updateShare: builder.mutation<Bucket, UpdateShareRequest>({
      invalidatesTags: ['Buckets'],
      query: (body) => ({url: '/api/bucket/share', method: 'PUT', body}),
    }),

    removeShare: builder.mutation<Bucket, {bucketId: string; userId: string}>({
      invalidatesTags: ['Buckets'],
      query: ({bucketId, userId}) => ({
        url: `/api/bucket/share/${bucketId}/${userId}`,
        method: 'DELETE',
      }),
    }),

    acceptShareLink: builder.mutation<Bucket, string>({
      invalidatesTags: ['Buckets'],
      query: (token) => ({
        url: '/api/bucket/acceptShareLink',
        method: 'POST',
        body: {token},
      }),
    }),
  }),
})

export const {
  useGetBucketsQuery,
  useLazyGetBucketsQuery,
  useGetBucketQuery,
  useLazyGetBucketQuery,
  useGetBucketByTokenQuery,
  useLazyGetBucketByTokenQuery,
  useCreateBucketMutation,
  useUpdateBucketMutation,
  useDeleteBucketMutation,
  useShareBucketMutation,
  useUpdateShareMutation,
  useRemoveShareMutation,
  useAcceptShareLinkMutation,
} = bucketApi
